/**
 * The Open dialog: browse saved templates and pick one to put on the canvas.
 *
 * Lives here rather than in the right pane because that pane describes the
 * CURRENT selection — design, data, JSON — while opening a template replaces
 * the whole document. It is a file action, so it gets a file-action surface,
 * and the extra width lets the thumbnails actually be readable.
 */

import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

import { DesignScreenPreviewComponent } from '../renderers/design-screen-preview.component';
import type { DesignScreen } from '../utils/design-publish.utils';

export interface TemplateGroupView {
  key: string;
  name: string;
  templates: Record<string, unknown>[];
}

export interface DesignOpenDialogData {
  groups: TemplateGroupView[];
  activeId: string;
  loading: boolean;
  error: string;
  previewScreen: (template: Record<string, unknown>) => DesignScreen | null;
  thumbnail: (template: Record<string, unknown>) => string;
}

export type DesignOpenDialogResult =
  | { action: 'open'; template: Record<string, unknown> }
  | { action: 'refresh' };

@Component({
  selector: 'app-design-open-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatIconModule,
    MatTooltipModule,
    DesignScreenPreviewComponent,
  ],
  templateUrl: './design-open-dialog.component.html',
  styleUrl: './design-open-dialog.component.scss',
})
export class DesignOpenDialogComponent {
  readonly data = inject<DesignOpenDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef = inject(MatDialogRef<DesignOpenDialogComponent, DesignOpenDialogResult>);

  readonly search = signal('');
  /** Folders start open here — the dialog has room, unlike the side pane. */
  private readonly collapsed = signal<ReadonlySet<string>>(new Set());

  readonly groups = computed<TemplateGroupView[]>(() => {
    const needle = this.search().trim().toLowerCase();
    if (!needle) return this.data.groups;

    return this.data.groups
      .map((group) => ({
        ...group,
        templates: group.templates.filter((template) =>
          String(template['name'] ?? '').toLowerCase().includes(needle),
        ),
      }))
      .filter((group) => group.templates.length > 0);
  });

  readonly total = computed(() =>
    this.groups().reduce((sum, group) => sum + group.templates.length, 0),
  );

  isCollapsed(key: string): boolean {
    return this.collapsed().has(key);
  }

  toggleGroup(key: string): void {
    this.collapsed.update((current) => {
      const next = new Set(current);
      if (!next.delete(key)) next.add(key);
      return next;
    });
  }

  open(template: Record<string, unknown>): void {
    this.dialogRef.close({ action: 'open', template });
  }

  refresh(): void {
    this.dialogRef.close({ action: 'refresh' });
  }

  name(template: Record<string, unknown>): string {
    return String(template['name'] ?? 'Untitled template');
  }

  kind(template: Record<string, unknown>): string {
    return template['pages'] ? 'Design template' : 'Saved component';
  }

  trackTemplate(_index: number, template: Record<string, unknown>): unknown {
    return template['id'];
  }

  trackGroup(_index: number, group: TemplateGroupView): string {
    return group.key;
  }
}
